import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

interface GradientCardProps {
  title: string;
  description: string;
  icon: React.ReactElement<any>;
  path: string;
  gradient: string;
  badge?: string;
  onClick?: () => void;
}

export const GradientCard: React.FC<GradientCardProps> = ({
  title, description, icon, path, gradient, badge
}) => {
  return (
    <StyledCard to={path} $gradient={gradient}>
      <CardOverlay />
      <CardContent>
        <CardHeader>
          <CardLeft>
            <CardTitle>{title}</CardTitle>
            <CardDesc>{description}</CardDesc>
          </CardLeft>
          <IconBox>
            {icon}
          </IconBox>
        </CardHeader>
        
        {badge && <Badge>{badge}</Badge>}
        
        <ViewDetails className="view-details">
          <span>View Details</span>
          <Arrow>→</Arrow>
        </ViewDetails>
      </CardContent>
    </StyledCard>
  );
};

const StyledCard = styled(Link)<{ $gradient: string }>`
  position: relative;
  overflow: hidden;
  background: ${props => props.$gradient};
  color: white;
  min-height: 180px;
  border-radius: 12px;
  text-decoration: none;
  display: block;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 40%;
    height: 100%;
    background: rgba(255, 255, 255, 0.08);
    border-radius: 50% 0 0 50%;
    transform: translateX(30%);
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);

    .view-details {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const CardOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  pointer-events: none;
`;

const CardContent = styled.div`
  position: relative;
  z-index: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const CardLeft = styled.div`
  flex: 1;
  padding-right: 12px;
`;

const CardTitle = styled.h3`
  font-weight: 700;
  font-size: 1.1rem;
  margin: 0 0 8px 0;
  color: white;
`;

const CardDesc = styled.p`
  font-size: 0.875rem;
  margin: 0;
  opacity: 0.85;
  color: white;
  line-height: 1.5;
`;

const IconBox = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  flex-shrink: 0;
  font-size: 28px;
  color: white;

  svg {
    width: 32px;
    height: 32px;
  }
`;

const Badge = styled.div`
  display: inline-block;
  padding: 4px 12px;
  background: rgba(255, 255, 255, 0.25);
  color: white;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 8px;
  letter-spacing: 0.3px;
`;

const ViewDetails = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 16px;
  margin-top: auto;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  opacity: 0.8;
  transform: translateY(5px);
  transition: all 0.3s ease;
  color: white;
  font-weight: 500;
  font-size: 13px;
`;

const Arrow = styled.span`
  font-size: 18px;
  font-weight: bold;
  transition: transform 0.3s ease;
  
  ${StyledCard}:hover & {
    transform: translateX(4px);
  }
`;

export default GradientCard;
